import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import { useContent } from '../contexts/ContentContext';
import LangSwitcher from './LangSwitcher';
import './Navbar.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { content } = useContent();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const branding = content.branding || {};
  const c = content.contact || {};

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // If user picked Turkish in /admin and navigates back to public pages,
  // auto-switch site to EN since public site only supports SK/EN.
  useEffect(() => {
    const code = (i18n.language || '').split('-')[0];
    const isPublic = !pathname.startsWith('/admin') && !pathname.startsWith('/login');
    if (isPublic && code === 'tr') {
      i18n.changeLanguage('en');
      document.documentElement.lang = 'en';
    }
  }, [pathname, i18n]);

  const links = [
    { to: '/', label: t('nav.home'), end: true },
    { to: '/about', label: t('nav.about') },
    { to: '/contact-us', label: t('nav.contact') },
  ];

  return (
    <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${open ? 'navbar--menu-open' : ''}`}>
      {open && <div className="navbar__backdrop" onClick={() => setOpen(false)} aria-hidden="true" />}

      <div className="container navbar__inner">
        <Link to="/" className="navbar__logo" onClick={() => setOpen(false)}>
          <img
            src={branding.logoUrl || '/logo.png'}
            alt="ZEL s.r.o."
            className="navbar__logo-img"
          />
        </Link>

        <nav className={`navbar__nav ${open ? 'navbar__nav--open' : ''}`}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
            >
              {l.label}
            </NavLink>
          ))}

          {/* Mobile-only contact info inside slide-out menu */}
          <div className="navbar__nav-footer">
            {c.phone && <a href={`tel:${c.phone.replace(/\s/g,'')}`}><FaPhoneAlt style={{ marginRight: 8, fontSize: '0.8em' }} /> {c.phone}</a>}
            {c.email && <a href={`mailto:${c.email}`}><FaEnvelope style={{ marginRight: 8, fontSize: '0.8em' }} /> {c.email}</a>}
          </div>
        </nav>

        <div className="navbar__actions">
          {/* Public Navbar: only Slovak + English (Turkish is admin-only) */}
          <LangSwitcher variant="dark" languages={['sk', 'en']} />

          <button
            className={`menu-toggle ${open ? 'menu-toggle--active' : ''}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            aria-expanded={open}
          >
            <span className="menu-toggle__bars">
              <span />
            </span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
